/**
 * Shared chrome for the unauthenticated screens (login, signup,
 * password-reset request/confirm, Google sign-in finish).
 *
 * Layout: full-viewport flex-center, dark ``--bg-0`` background, a
 * single 380px ``vz-auth-card`` containing a brand row + tagline +
 * title + the screen's own children. Each screen passes its title
 * and an optional footer slot.
 */

import type { ReactNode } from "react";

import { Icon } from "../lib/icons";
import { useT } from "../lib/i18n";

interface AuthShellProps {
  /** Title shown above the form. Falls back to ``T("appTitle")``. */
  title?: string;
  /** Form / body content. */
  children: ReactNode;
  /** Footer slot (links, "back to login", etc.). */
  footer?: ReactNode;
  /** Override aria-label for the surrounding region. */
  ariaLabel?: string;
}

export function AuthShell({
  title,
  children,
  footer,
  ariaLabel,
}: AuthShellProps): JSX.Element {
  const [, T] = useT();
  return (
    <div className="vz-auth-shell">
      <section
        className="vz-auth-card"
        aria-label={ariaLabel ?? title ?? T("appTitle")}
      >
        <div className="vz-auth-brand">
          <div className="vz-auth-mark">
            <Icon.brand
              style={{ width: 20, height: 20, color: "var(--accent)" }}
            />
            <span className="vz-mono">
              vuzlyk<span className="vz-brand-cur">_</span>
            </span>
          </div>
          <span className="vz-auth-tag">{T("appTagline")}</span>
        </div>
        {title !== undefined && <h1 className="vz-auth-title">{title}</h1>}
        {children}
        {footer !== undefined && (
          <div className="vz-auth-footer">{footer}</div>
        )}
      </section>
    </div>
  );
}
